"use client";

import { type FC, type Ref, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AskAIButton,
  Button,
  ExpansionPanel,
  Flex,
  FormSectionContext,
  Icon,
  IconButton,
  Spacing,
  Stack,
  Text,
  Tooltip,
  transitionDurations,
  useForm,
  usePubSub,
  type FormFieldSchema,
  type UserPromptRecord,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";

import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components";
import {
  useTemplates,
  useAgentFormAwareness,
  useAutoScrollToPanel,
} from "../../hooks";
import { useTemplateParent } from "../../contexts/TemplateParentContext";
import { extractFieldIDs } from "../../utils";

export type FormFlowTemplateProps = {
  /**
   * The templates to render above the subforms
   */
  templates?: TemplateCollection;
  /**
   * The subforms to render within the flow
   */
  subforms: FormFlowSubform[];
  /**
   * The label for the submit button.
   * @default "Bevestigen"
   */
  buttonValue?: string;
  /**
   * The type of form flow.
   * - "new": Edit button only shows for completed subforms
   * - "existing": Edit button shows for all non-current subforms
   * @default "new"
   */
  type?: "new" | "existing";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export type FormFlowSubform = {
  /**
   * Unique identifier for the subform
   */
  id: string;
  /**
   * The title of the subform
   */
  title: string;
  /**
   * The description of the subform
   */
  description?: string;
  /**
   * The help prompt to render in the pane
   */
  helpPrompt?: UserPromptRecord;
  /**
   * The templates to render within the subform
   */
  templates?: TemplateCollection;
};

/**
 * A template wrapper for the FormFlow container component.
 */
export const FormFlowTemplate: FC<FormFlowTemplateProps> = ({
  subforms,
  templates,
  buttonValue,
  type = "new",
  ref,
}) => {
  const { t } = useTranslation();
  const { lockPrompts, lockUI } = useTemplates();
  const pubSub = usePubSub();
  const form = useForm();
  const templateParent = useTemplateParent();

  if (!form) {
    throw new Error("FormFlowTemplate must be used within a Form component");
  }

  const [currentFieldSchema, setCurrentFieldSchema] = useState<
    FormFieldSchema[]
  >([]);

  // Re-read field schema when form state changes (field registration, value changes, errors).
  // form.errors is a reactive proxy that updates on validation/registration events.
  // Note: form.getFieldSchema is excluded — it's a method reference whose identity
  // depends on the Provider implementation and doesn't reliably signal changes.
  useEffect(() => {
    setCurrentFieldSchema(form.getFieldSchema());
    // schemaVersion increments on field registration, deregistration,
    // and value changes — it replaces the accidental dependency on the
    // unstable form context object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.schemaVersion]);

  /**
   * Track the currently open panel index
   * First panel is open by default
   */
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);

  const panelContainerRef = useRef<HTMLDivElement>(null);
  const controlPrefix = `${templateParent?.id ?? "__flow"}__`;

  useAutoScrollToPanel({
    containerRef: panelContainerRef,
    currentPanelIndex,
    controlPrefix,
  });

  /**
   * Build panel metadata so the AI agent knows about each collapsible section
   */
  const panelsMeta = useMemo(
    () =>
      subforms.map((sf) => ({
        key: sf.id,
        title: sf.title,
        ...(sf.description ? { description: sf.description } : {}),
      })),
    [subforms],
  );

  const wizardParam = useMemo(
    () => ({
      currentStep: subforms[currentPanelIndex]?.id ?? "",
      currentStepIndex: currentPanelIndex,
      steps: panelsMeta,
      totalSteps: subforms.length,
    }),
    [subforms, currentPanelIndex, panelsMeta],
  );

  useAgentFormAwareness({
    formId: templateParent?.id ?? "__flow",
    formType: "wizard",
    fieldSchema: currentFieldSchema,
    wizard: wizardParam,
  });

  /**
   * Determines if the edit button should be shown for a given panel index.
   * - For "existing" type: show on all panels except the current one
   * - For "new" type: show only on completed subforms (before current)
   */
  const shouldShowEditButton = (index: number) => {
    if (index === currentPanelIndex) return false;
    if (type === "existing") return true;
    // type === "new": only show for completed subforms (not future ones)
    return index < currentPanelIndex;
  };

  const handleHelpPrompt = (helpPrompt?: UserPromptRecord) => {
    if (helpPrompt && !lockPrompts) {
      pubSub.publish("sendPrompt", {
        value: helpPrompt,
      });
    }
  };

  /**
   * Validates the current subform's fields and advances to the next panel if valid
   */
  const handleNextSubform = () => {
    const currentSubform = subforms[currentPanelIndex];
    const fieldIDs = extractFieldIDs({ templates: currentSubform.templates });

    const { valid } = form.validateFields(fieldIDs);

    // console.log("[FormFlowTemplate] validateFields result", {
    //   valid,
    //   fieldIDs,
    //   form,
    // });

    if (valid) {
      setCurrentPanelIndex(
        Math.min(currentPanelIndex + 1, subforms.length - 1),
      );
    }
    // Errors are automatically set in Form state and displayed
  };

  /**
   * Handle agent control fields: nextPanel, submitForm, and panel expand.
   * Listens for inputUpdate events to allow the AI agent to navigate panels,
   * submit the form, or expand a specific panel.
   */
  useEffect(() => {
    const formId = templateParent?.id ?? "__flow";
    const prefix = `${formId}__`;

    const unsubscribe = pubSub.subscribe("inputUpdate", (payload) => {
      if (!payload) return;
      const updates = Array.isArray(payload) ? payload : [payload];
      for (const update of updates) {
        if (!update?.id) continue;

        if (update.id === `${prefix}nextPanel`) {
          handleNextSubform();
          return;
        }

        if (update.id === `${prefix}previousPanel`) {
          setCurrentPanelIndex((prev) => Math.max(0, prev - 1));
          return;
        }

        if (update.id === `${prefix}submitForm`) {
          submitButtonRef.current?.click();
          return;
        }

        if (update.id.startsWith(prefix) && update.id.endsWith("__expand")) {
          const subformId = update.id.slice(prefix.length, -"__expand".length);
          const index = subforms.findIndex((sf) => sf.id === subformId);
          if (index >= 0) {
            setCurrentPanelIndex(index);
          }
        }
      }
    });
    return unsubscribe;
  }, [subforms, pubSub, templateParent?.id, handleNextSubform]);

  /**
   * Re-report UI context after a panel switch so on_screen values
   * reflect the newly opened/closed panels. Delayed to run after
   * the ExpansionPanel collapse animation completes and aria-hidden
   * is updated.
   */
  const { notifyValueChanged } = form;

  useEffect(() => {
    const ANIMATION_BUFFER_MS = 50;
    const timer = setTimeout(
      () => {
        notifyValueChanged();
      },
      transitionDurations.m * 1000 + ANIMATION_BUFFER_MS,
    );
    return () => clearTimeout(timer);
  }, [currentPanelIndex, notifyValueChanged]);

  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const subformContentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [subformErrors, setSubformErrors] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const subform of subforms) {
      const el = subformContentRefs.current[subform.id];
      next[subform.id] = !!el?.querySelector("[data-error]");
    }
    setSubformErrors(next);
  }, [subforms, form.errors]);

  return (
    <Flex direction="column" gap="s" ref={ref}>
      {templates && <TemplatesParser templates={templates} level={1} />}

      <Flex direction="column" gap="s" ref={panelContainerRef}>
        {subforms?.map((subform, index) => (
          <ExpansionPanel
            key={subform.id}
            panelIndex={index}
            open={index === currentPanelIndex}
            customControlled
            title={
              <Text variant="bodyM" bold grow>
                {subform.title}
              </Text>
            }
            variant="navigation"
            padding="s"
            gap="s"
            borderRadius="xs"
            border="none"
            forceMount
            trailing={
              <Flex align="center" gap="xs" style={{ minHeight: rem(36) }}>
                {subformErrors[subform.id] && index !== currentPanelIndex && (
                  <Tooltip value={t("formHasErrors")}>
                    <Icon icon="alertFilled" color="warning" />
                  </Tooltip>
                )}
                <Stack>
                  {index === currentPanelIndex && subform.helpPrompt && (
                    <AskAIButton
                      onClick={() => handleHelpPrompt(subform.helpPrompt)}
                      disabled={lockPrompts}
                    />
                  )}
                  {shouldShowEditButton(index) && (
                    <Tooltip value={t("edit")}>
                      <IconButton
                        icon="edit"
                        variant="background"
                        size="xs"
                        border="default"
                        onClick={() => setCurrentPanelIndex(index)}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </Flex>
            }
            customContent={
              <FormSectionContext.Provider value={subform.id}>
                <div
                  ref={(el) => {
                    subformContentRefs.current[subform.id] = el;
                  }}
                >
                  {subform.description && (
                    <Text
                      variant="bodyS"
                      color="currentColor50"
                      style={{ maxWidth: "75%" }}
                    >
                      {subform.description}
                    </Text>
                  )}
                  <Spacing height="s" />
                  {subform.templates && (
                    <TemplatesParser templates={subform.templates} level={1} />
                  )}
                  <Spacing height="s" />
                  <Flex justify="flex-end">
                    {index === subforms.length - 1 ? (
                      <Button
                        ref={submitButtonRef}
                        type="submit"
                        variant="primary"
                        disabled={lockUI}
                      >
                        {buttonValue ?? t("templating.confirm")}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={lockUI}
                        onClick={handleNextSubform}
                      >
                        {t("next")}
                      </Button>
                    )}
                  </Flex>
                </div>
              </FormSectionContext.Provider>
            }
          />
        ))}
      </Flex>
    </Flex>
  );
};
